import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { ICON_MEDIUM_SIZE, STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import React, { ElementType, FunctionComponent, PropsWithChildren, ReactNode } from "react";
import { Transition } from "react-transition-group";
import { BoxProps } from "../box/Box";
import IconButton from "../button/IconButton";
import Text from "../text/Text";
import FlexColumn from "./FlexColumn";

const duration = 300;
const halfIconWidth = ICON_MEDIUM_SIZE / 2;
const forceDisplay = { display: "inline-flex" };

const PREFIX = "FlexSideDrawer";

const classes = {
	button: `${PREFIX}button`,
	drawer: `${PREFIX}drawer`,
	middle: `${PREFIX}middle`,
	drawerRight: `${PREFIX}drawerRight`,
};

const StyledBox = styled(Box)(({
	theme,
}) => ({
	[`& .${classes.button}`]: {
		// background is necessary for button so that pane border does not show through button
		backgroundColor: theme.palette.background.paper,
		display: "none",
		right: -halfIconWidth,
		position: "absolute",
		zIndex: 1,

		// top position may need to be property into component
		top: 16,

		"&.force": forceDisplay,
	},
	[`& .${classes.drawer}`]: {
		transition: `width ${duration}ms`,

		"&:hover + * > button": forceDisplay,
	},
	[`& .${classes.middle}`]: {
		// currently bug does not allow use of borderRight and borderColor together on Box:
		// https://github.com/mui-org/material-ui/issues/16995
		borderLeft: STANDARD_BORDER_STYLE,
		position: "relative",

		"&:hover > button": forceDisplay,
	},
	[`&.${classes.drawerRight}`]: {
		flexDirection: "row-reverse",
	},
}));

export interface FlexSideDrawerProps extends PropsWithChildren {
	/** node to render in drawer when open */
	drawerNode: ReactNode;

	/** node to render in drawer when closed (defaults to drawerNode if not provided) */
	drawerNodeWhenClosed?: ReactNode;

	/** HTML tag to use for component */
	component?: ElementType;

	/** determine if button will be hidden for expanded state and only appear on hover vs always visible - true is always visible */
	buttonAlwaysVisible?: boolean;

	/** hides the open/close button */
	disabled?: boolean;

	/** indicates if drawer is expanded */
	open: boolean;

	/** css class for button open */
	openButtonClassName?: string;

	/** label for button open */
	openButtonLabel?: string;

	/** determine if drawer located on left side (false) or right side (true)*/
	rightDrawer?: boolean;

	/* width of drawer when closed */
	widthClosed?: number;

	/** width of drawer when open */
	widthOpen?: number;

	/** callback when open state of drawer is changed */
	onToggleOpen?: (open: boolean) => void;

	/** callback when drawer finishes its transition */
	onTransition?: () => void;
};

const FlexSideDrawer: FunctionComponent<FlexSideDrawerProps> = (props) => {
	const {
		buttonAlwaysVisible = false,
		children,
		component,
		disabled,
		drawerNode,
		drawerNodeWhenClosed,
		open,
		openButtonClassName = "",
		openButtonLabel = "",
		rightDrawer = false,
		widthClosed,
		widthOpen,
		onToggleOpen,
		onTransition,
	} = props;

	const buttonClass = `${classes.button} ${(open && !buttonAlwaysVisible) ? "" : "force"} ${open ? "" : openButtonClassName}`;
	const maxWidth = widthOpen || 256;
	const minWidth = widthClosed || ICON_MEDIUM_SIZE;
	const rootProps: BoxProps = component ? { component } : null;

	const transitionStyles = {
		exited: { width: minWidth },
		exiting: { width: minWidth },
	};

	function onClick() {
		onToggleOpen(!open);

		if (onTransition) {
			setTimeout(onTransition, duration);
		}
	}

	// NOTE: Box with full width is necessary inside transition div to keep the content
	// from wrapping in any way while collapsing the pane

	return (
		<StyledBox
			className={rightDrawer ? classes.drawerRight : ""}
			display="flex"
			height={1}
			overflow="hidden"
			{...rootProps}
		>
			<Transition in={open} timeout={duration}>
				{(state) => (
					<Box
						className={classes.drawer}
						flexShrink={0}
						overflow="hidden"
						style={transitionStyles[state]}
						width={maxWidth}
					>
						{state !== "exited" ? (
							<Box height={1} width={maxWidth}>
								{drawerNode}
							</Box>
						)
							:
							drawerNodeWhenClosed
						}
					</Box>
				)}
			</Transition>
			<Box className={classes.middle}>
				{!disabled && (
					<IconButton
						aria-label={open ? "collapse" : "expand"}
						className={buttonClass}
						design="outline"
						onClick={onClick}
						round
					>
						{(open != rightDrawer) ?
							<ChevronLeft />
							:
							<ChevronRight />
						}
						{!open && openButtonLabel &&
							<Text className="csi-drawer-button-label" text={openButtonLabel} />
						}
					</IconButton>
				)}
			</Box>
			<FlexColumn
				flexGrow={1}
				overflow="auto"
			>
				{children}
			</FlexColumn>
		</StyledBox>
	);
};

export default FlexSideDrawer;
